import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable()
export class F1065SchM3Service {

  constructor(private http: HttpClient) {}

  /**
   * Load main Schedule M3 form data
   * Uses loadPivotFormJson with action_id: 31259
   * Mirrors AngularJS: _.merge({}, GlobalService.globalParams, filterParams, _params)
   */
  loadFormData(
    baseUrl: string,
    rowData: any,
    filterParams: any,
    globalParams: any
  ): Observable<any> {
    const url = `${baseUrl}/loadPivotFormJson`;
    
    // Start with globalParams as base (like AngularJS does)
    let params: any = {};
    
    // First merge globalParams
    if (globalParams) {
      Object.keys(globalParams).forEach(key => {
        if (globalParams[key] !== undefined && globalParams[key] !== null) {
          params[key] = globalParams[key];
        }
      });
    }
    
    // Then merge filterParams (overrides globalParams)
    if (filterParams) {
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] !== undefined && filterParams[key] !== null) {
          params[key] = filterParams[key];
        }
      });
    }
    
    // Finally set our specific params (override everything)
    params.action_id = '31259';
    params.tax_year = rowData.TAX_YEAR;
    params.scenario = rowData.SCENARIO_KEY;
    params.jcd_key = rowData.JCD_KEY;
    params.combination_key = rowData.COMBINATION_KEY;
    params.schedule_name = 'M3';
    params.part_no = 'P1';

    console.log('[F1065-Sch-M3] loadFormData final params:', params);

    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Load subtable records
   * action_code: z57kmr
   * Mirrors AngularJS: _.merge({}, GlobalService.globalParams, filterParams, _params)
   */
  loadSubTableData(
    baseUrl: string,
    rowData: any,
    filterParams: any,
    globalParams: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    // Start with globalParams as base (like AngularJS does)
    let params: any = {};
    
    // First merge globalParams
    if (globalParams) {
      Object.keys(globalParams).forEach(key => {
        if (globalParams[key] !== undefined && globalParams[key] !== null) {
          params[key] = globalParams[key];
        }
      });
    }
    
    // Then merge filterParams (overrides globalParams)
    if (filterParams) {
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] !== undefined && filterParams[key] !== null) {
          params[key] = filterParams[key];
        }
      });
    }
    
    // Finally set our specific params (override everything)
    params.action_code = 'z57kmr';
    params.combination_key = rowData.COMBINATION_KEY;
    params.schedule = 'M3';

    console.log('[F1065-Sch-M3] loadSubTableData final params:', params);

    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Search for business info (autocomplete)
   * action_code: 1foxtj
   * Mirrors AngularJS: _.merge({}, GlobalService.globalParams, filterParams, _params)
   */
  searchBusinessInfo(
    baseUrl: string,
    searchVal: string,
    sourceType: string,
    filterParams: any,
    globalParams: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    
    // Start with globalParams as base (like AngularJS does)
    let params: any = {};
    
    // First merge globalParams
    if (globalParams) {
      Object.keys(globalParams).forEach(key => {
        if (globalParams[key] !== undefined && globalParams[key] !== null) {
          params[key] = globalParams[key];
        }
      });
    }
    
    // Then merge filterParams (overrides globalParams)
    if (filterParams) {
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] !== undefined && filterParams[key] !== null) {
          params[key] = filterParams[key];
        }
      });
    }
    
    // Finally set our specific params (override everything)
    params.action_code = '1foxtj';
    params.search_val = searchVal;
    params.source_type = sourceType;

    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Save Schedule M3 form data
   * Payload must match AngularJS structure EXACTLY as FORM DATA
   * AngularJS uses: _.merge({}, GlobalService.globalParams, filterParams)
   * then _.extend with jsonObj, jsonSettings, process_name, combination_key, trans_type_key
   */
  saveScheduleM3Form(
    baseUrl: string,
    rowData: any,
    formData: any[],
    filterParams: any,
    processName: string,
    globalParams: any
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=tkgaam`;

    const transTypeKey = formData[0]?.editrows?.[0]?.[0]?.trans_type_key || '';

    const jsonSettings = JSON.stringify({
      tax_year: rowData.TAX_YEAR,
      scenario: rowData.SCENARIO_KEY,
      jcd_key: rowData.JCD_KEY,
      combination_key: rowData.COMBINATION_KEY,
      trans_type_key: transTypeKey
    });

    const jsonObj = JSON.stringify(formData);

    // Build checkLockedbj
    const checkLockedbj = JSON.stringify([{
      leid: rowData.LEID,
      combination_key: rowData.COMBINATION_KEY,
      scenario_key: rowData.SCENARIO_KEY
    }]);

    // Build form data matching AngularJS payload EXACTLY
    // AngularJS does: params = _.merge({}, GlobalService.globalParams, filterParams)
    // then _.extend with jsonObj, jsonSettings, process_name, combination_key, trans_type_key
    let formParams = new HttpParams({ encoder: new CustomHttpParamEncoder() });
    
    // jsonSettings and jsonObj first (as per AngularJS _.extend order)
    formParams = formParams.set('jsonSettings', jsonSettings);
    formParams = formParams.set('jsonObj', jsonObj);
    
    // GlobalService.globalParams - use directly, these come from the service
    if (globalParams) {
      if (globalParams.tax_year !== undefined) formParams = formParams.set('tax_year', String(globalParams.tax_year));
      if (globalParams.screen_key !== undefined) formParams = formParams.set('screen_key', String(globalParams.screen_key));
      if (globalParams.scenario !== undefined) formParams = formParams.set('scenario', String(globalParams.scenario));
      if (globalParams.jcd_key !== undefined) formParams = formParams.set('jcd_key', String(globalParams.jcd_key));
      if (globalParams.jcd_ta_key !== undefined) formParams = formParams.set('jcd_ta_key', String(globalParams.jcd_ta_key));
      if (globalParams.activity_key !== undefined) formParams = formParams.set('activity_key', String(globalParams.activity_key));
      if (globalParams.screen_type !== undefined) formParams = formParams.set('screen_type', String(globalParams.screen_type));
      if (globalParams.is_locked !== undefined) formParams = formParams.set('is_locked', String(globalParams.is_locked));
      if (globalParams.issue_id !== undefined) formParams = formParams.set('issue_id', String(globalParams.issue_id));
      if (globalParams.issue_short_desc !== undefined) formParams = formParams.set('issue_short_desc', String(globalParams.issue_short_desc));
      if (globalParams.issue_id_for_tx_save !== undefined) formParams = formParams.set('issue_id_for_tx_save', String(globalParams.issue_id_for_tx_save));
    }
    
    formParams = formParams.set('checkLockedbj', checkLockedbj);
    
    // FilterParams - merged after globalParams - ALWAYS include scenarioCode and is_issue_key_enabled
    const scenarioCode = filterParams?.scenarioCode || globalParams?.scenarioCode || 'RAF';
    const isIssueKeyEnabled = filterParams?.is_issue_key_enabled || globalParams?.is_issue_key_enabled || 'N';
    
    formParams = formParams.set('scenarioCode', String(scenarioCode));
    formParams = formParams.set('is_issue_key_enabled', String(isIssueKeyEnabled));
    
    if (filterParams?.parent_me_string !== undefined) {
      formParams = formParams.set('parent_me_string', String(filterParams.parent_me_string));
    }
    
    // Final extend values (override any previous)
    formParams = formParams.set('process_name', processName || '1065 Textual Information');
    formParams = formParams.set('combination_key', String(rowData.COMBINATION_KEY));
    formParams = formParams.set('trans_type_key', String(transTypeKey));

    console.log('[F1065-Sch-M3] Save form data:', formParams.toString());

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, formParams.toString(), {
      headers,
      withCredentials: true
    });
  }
}

// Custom encoder to handle special characters properly
class CustomHttpParamEncoder {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

